import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import Cookies from "js-cookie";
import Nav from "../../components/Home/Nav";
import Footer from "../../components/Home/Footer";
import {
  HiUser,
  HiMail,
  HiPhone,
  HiShieldCheck,
  HiLockClosed,
  HiLogout,
  HiPencilAlt,
  HiCheck,
  HiArrowLeft,
  HiCamera,
  HiExternalLink,
  HiBadgeCheck,
} from "react-icons/hi";

function Profile() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [imgError, setImgError] = useState(false);
  const [user, setUser] = useState({
    id: "",
    name: "",
    email: "",
    phone: "",
    picture: "",
    role_id: 2,
    role_name: "User",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    picture: "",
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = Cookies.get("accessToken");
      if (!token) {
        toast.error("Please sign in to view your profile");
        navigate("/login");
        return;
      }

      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await axios.get(`${apiUrl}/api/profile`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setUser(response.data);
      setFormData({
        name: response.data.name || "",
        phone: response.data.phone || "",
        picture: response.data.picture || "",
      });
    } catch (error) {
      console.error("Error loading profile:", error);
      toast.error("Failed to load profile details");
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name, email) => {
    let displayName = name;
    if (!displayName || displayName === "User" || displayName === "N/A") {
      if (email) {
        displayName = email.split("@")[0];
      } else {
        return "U";
      }
    }
    const cleanName = displayName.replace(/[^a-zA-Z0-9\s]/g, "").trim();
    const parts = cleanName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    } else if (parts.length === 1) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return "U";
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const token = Cookies.get("accessToken");
      const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";
      const response = await axios.put(
        `${apiUrl}/api/profile`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      toast.success("Profile updated successfully!");
      setUser(response.data.user);
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setUpdating(false);
    }
  };

  const handleLogout = () => {
    Cookies.remove("accessToken");
    Cookies.remove("roleId");
    Cookies.remove("userId");
    navigate("/");
    toast.success("Logged out successfully");
  };

  const isGoogleAccount = user.picture && user.picture.includes("googleusercontent.com");

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      <Nav />

      <main className="flex-grow pt-28 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header Banner */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-xs font-bold text-slate-500 hover:text-blue-600 transition-colors"
            >
              <HiArrowLeft size={16} /> Back
            </button>
            <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
              User Profile
            </span>
          </div>

          {loading ? (
            <div className="glass rounded-3xl p-12 text-center shadow-xl">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-sm font-semibold text-slate-600">Loading profile data...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Profile Card Header */}
              <div className="bg-white rounded-3xl p-6 md:p-8 border border-slate-100 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-bl from-blue-500/10 to-transparent rounded-full blur-3xl pointer-events-none"></div>

                <div className="flex flex-col md:flex-row items-center md:items-start gap-6 relative z-10">
                  {/* Avatar */}
                  <div className="relative group">
                    <div className="w-28 h-28 md:w-32 md:h-32 rounded-3xl overflow-hidden shadow-2xl border-4 border-white bg-slate-900 flex items-center justify-center text-white font-black text-3xl">
                      {user.picture && !imgError ? (
                        <img
                          src={user.picture}
                          alt={user.name}
                          referrerPolicy="no-referrer"
                          className="w-full h-full object-cover"
                          onError={() => setImgError(true)}
                        />
                      ) : (
                        <span>{getInitials(user.name, user.email)}</span>
                      )}
                    </div>
                    {isGoogleAccount && (
                      <div
                        className="absolute -bottom-2 -right-2 bg-white text-blue-600 p-1.5 rounded-full shadow-lg border border-slate-100"
                        title="Google Verified Account"
                      >
                        <HiBadgeCheck size={20} className="text-blue-500" />
                      </div>
                    )}
                  </div>

                  {/* Info Summary */}
                  <div className="flex-1 text-center md:text-left space-y-2">
                    <div className="flex flex-wrap items-center justify-center md:justify-start gap-3">
                      <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">
                        {user.name || "User"}
                      </h1>
                      <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-wider ${
                        user.role_name === "Admin" || user.role_name === "Super Admin"
                          ? "bg-purple-100 text-purple-700"
                          : user.role_name === "Staff"
                          ? "bg-amber-100 text-amber-700"
                          : "bg-blue-100 text-blue-700"
                      }`}>
                        {user.role_name || "User"}
                      </span>
                    </div>

                    <p className="text-sm font-semibold text-slate-500 flex items-center justify-center md:justify-start gap-2">
                      <HiMail size={16} className="text-blue-500" />
                      {user.email}
                    </p>

                    {isGoogleAccount && (
                      <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                        <img
                          src="https://www.svgrepo.com/show/475656/google-color.svg"
                          alt="Google"
                          className="w-3.5 h-3.5"
                        />
                        <span>Linked via Google Account</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-col gap-2 w-full md:w-auto">
                    {!isGoogleAccount && (
                      <button
                        onClick={() => setIsEditing(!isEditing)}
                        className="px-5 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-xs hover:bg-blue-700 transition-all flex items-center justify-center gap-2 shadow-lg shadow-blue-100 active:scale-95"
                      >
                        <HiPencilAlt size={16} />
                        {isEditing ? "Cancel Edit" : "Edit Profile"}
                      </button>
                    )}
                    {(user.role_name === "Admin" || user.role_name === "Super Admin" || user.role_name === "Staff") && (
                      <Link
                        to={user.role_name === "Admin" || user.role_name === "Super Admin" ? "/admin/dashboard" : "/staffs"}
                        className="px-5 py-2.5 bg-slate-900 text-white rounded-xl font-bold text-xs hover:bg-slate-800 transition-all flex items-center justify-center gap-2 text-center active:scale-95"
                      >
                        <HiExternalLink size={16} />
                        {user.role_name === "Admin" || user.role_name === "Super Admin" ? "Admin Dashboard" : "Staff Console"}
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              {/* Edit Profile Form - MANUAL LOGIN ONLY */}
              {!isGoogleAccount && isEditing && (
                <form
                  onSubmit={handleUpdate}
                  className="bg-white rounded-3xl p-6 md:p-8 border border-blue-200 shadow-xl animate-in fade-in slide-in-from-top-4 duration-300 space-y-6"
                >
                  <h3 className="text-base font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-3">
                    Update Profile Details
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Full Name
                      </label>
                      <div className="relative">
                        <HiUser className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                          placeholder="Your Name"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Phone Number
                      </label>
                      <div className="relative">
                        <HiPhone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="text"
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                          placeholder="+977 98XXXXXXX"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                        Profile Image URL
                      </label>
                      <div className="relative">
                        <HiCamera className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                          type="url"
                          value={formData.picture}
                          onChange={(e) => setFormData({ ...formData, picture: e.target.value })}
                          className="w-full pl-11 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold focus:outline-none focus:border-blue-500 focus:bg-white transition-all"
                          placeholder="https://images.unsplash.com/..."
                        />
                      </div>
                      <p className="text-[11px] text-slate-400">
                        Paste a direct URL to your profile photo image.
                      </p>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t border-slate-100">
                    <button
                      type="button"
                      onClick={() => setIsEditing(false)}
                      className="px-6 py-2.5 bg-slate-100 text-slate-700 rounded-xl font-bold text-xs hover:bg-slate-200 transition-all"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-xl font-bold text-xs hover:bg-blue-700 transition-all flex items-center gap-2 disabled:opacity-70"
                    >
                      {updating ? (
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <HiCheck size={16} />
                      )}
                      Save Changes
                    </button>
                  </div>
                </form>
              )}

              {/* Detailed Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Account Details */}
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-xl space-y-4">
                  <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2">
                    <HiShieldCheck className="text-blue-500" size={18} />
                    Account Overview
                  </h2>

                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-slate-50">
                      <span className="text-xs font-semibold text-slate-500">Account ID</span>
                      <span className="text-xs font-black text-slate-900">#{user.id}</span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-slate-50">
                      <span className="text-xs font-semibold text-slate-500">Full Name</span>
                      <span className="text-xs font-black text-slate-900">{user.name || "N/A"}</span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-slate-50">
                      <span className="text-xs font-semibold text-slate-500">Email Address</span>
                      <span className="text-xs font-black text-slate-900">{user.email}</span>
                    </div>

                    <div className="flex justify-between items-center py-2 border-b border-slate-50">
                      <span className="text-xs font-semibold text-slate-500">Phone Number</span>
                      <span className="text-xs font-black text-slate-900">{user.phone || "Not specified"}</span>
                    </div>

                    <div className="flex justify-between items-center py-2">
                      <span className="text-xs font-semibold text-slate-500">Access Role</span>
                      <span className="text-xs font-black text-blue-600">{user.role_name || "Standard User"}</span>
                    </div>
                  </div>
                </div>

                {/* Security & Actions */}
                <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-xl space-y-4 flex flex-col justify-between">
                  <div>
                    <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest flex items-center gap-2 mb-4">
                      <HiLockClosed className="text-blue-500" size={18} />
                      Security & Preferences
                    </h2>

                    {isGoogleAccount ? (
                      <div className="p-4 bg-blue-50 border border-blue-100 rounded-2xl space-y-2">
                        <div className="flex items-center gap-2 text-blue-700 font-bold text-xs">
                          <img
                            src="https://www.svgrepo.com/show/475656/google-color.svg"
                            alt="Google"
                            className="w-4 h-4"
                          />
                          <span>Google Authentication Active</span>
                        </div>
                        <p className="text-xs text-blue-600/80 leading-relaxed">
                          Your profile and authentication security are managed directly by your linked Google Account. Password changes and profile editing are disabled for Google-managed accounts.
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className="text-xs text-slate-500 leading-relaxed mb-6">
                          Manage your password settings or safely log out from your active session across devices.
                        </p>

                        <div className="space-y-3">
                          <Link
                            to="/change-password"
                            className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-blue-50 hover:border-blue-200 border border-slate-100 rounded-2xl transition-all group"
                          >
                            <div className="flex items-center gap-3">
                              <HiLockClosed className="text-slate-400 group-hover:text-blue-600" size={20} />
                              <span className="text-xs font-bold text-slate-800 group-hover:text-blue-600">
                                Change Password
                              </span>
                            </div>
                            <span className="text-xs font-bold text-blue-600">Update →</span>
                          </Link>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="pt-4 border-t border-slate-100">
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center justify-center gap-2 p-3.5 bg-red-50 text-red-600 hover:bg-red-100 rounded-2xl font-bold text-xs transition-all active:scale-95"
                    >
                      <HiLogout size={18} />
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Profile;
